import { Suspense, useCallback, useState, useEffect, useContext } from "react";
import { Button, SnackbarCloseReason, Grid, Paper, Tooltip, Chip } from "@material-ui/core";
import { SnackbarAPI } from "../../../../shared";
import { SnackbarProps } from "../../../../../@types/snackbarAPI.types";

import PublishIcon from "@material-ui/icons/Publish";
import SendIcon from "@material-ui/icons/Send";
import downloadFile from "../../../../../utils/commonFunctions/downloadFile";
import shortenFileName from "../../../../../utils/commonFunctions/shortenFileName";
import { trackPromise } from "react-promise-tracker";
import excelToJson from "../../../../../utils/commonFunctions/excelToJson";
import { LINK } from "../../../../../config/config";
import JSONToExcel from "../../../../../utils/commonFunctions/JSONToExcel";
import { AuthContext, UIContext } from "../../../../../contexts";
import { memberBulkUploadTemplateFile } from "../../../../../assets";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import axiosInstance from "../../../../../utils/commonFunctions/axiosInstance";

const VEBTermination = (): JSX.Element => {
	const { user } = useContext(AuthContext);
	const { setDashboardHeader } = useContext(UIContext);
	const [excelFile, setExcelFile] = useState<any>(null);
	const [hasSubmitClick, setHasCreateClick] = useState<boolean>(false);
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (
			event: React.SyntheticEvent<Element, Event> | Event | React.SyntheticEvent<any, Event>,
			reason?: SnackbarCloseReason | undefined
		) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleDocumentChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = event.target;

		if (files) {
			setExcelFile(files[0] as any);
			event.target.value = "";
		}
	}, []);

	const handleDeleteDocument = useCallback(() => {
		setExcelFile(null);
	}, []);

	const handleSubmitClick = async () => {
		try {
			if (excelFile !== null) {
				const JSONData = await trackPromise(excelToJson(excelFile));
				// Excel parser
				// console.log("JSONData", JSONData);
				const bulkUploadRes = await axiosInstance.put(
					`${LINK}/member/bulk-upload/VEB-termination/220`,
					{
						excel_data: JSONData
					},
					{
						headers: {
							Authorization: `Bearer ${localStorage.getItem("@jwt")}`
						}
					}
				);
				setTimeout(() => {
					setSnackbarAPICallProps(
						Object.assign({}, snackbarAPICallProps, {
							open: true,
							message: "Successfully Terminated VEB Enrollments",
							severity: "success"
						})
					);
				}, 2000);
			}
		} catch (err: any) {
			if (err.response.status === 400) {
				if (err.response.data.err) {
					const fileName = "Error Report";

					const excelPayload = {
						user: {
							role: user?.role,
							name: user?.first_name + " " + user?.last_name
						},
						reportFieldDetails: {
							group_number: 220
						},
						response: err.response.data.err.errorData
					};

					await trackPromise(JSONToExcel(excelPayload, fileName));

					setTimeout(() => {
						setSnackbarAPICallProps(
							Object.assign({}, snackbarAPICallProps, {
								open: true,
								message: "Failed to load File. See Error Report!",
								severity: "error"
							})
						);
					}, 2000);
				}
			}
		}
	};

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.veb_termination_bulk_upload);
	}, [setDashboardHeader]);

	return (
		<div className="create-dependent" id="create-dependent">
			<Suspense fallback={<div />}>
				<SnackbarAPI snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<>
				<div className="pf-action-button-container" id="pf-action-button-container">
					<Button
						className="button-green"
						onClick={handleSubmitClick}
						variant="contained"
						// disabled={hasSubmitClick}
						// disableElevation={hasSubmitClick}
						style={{
							cursor: hasSubmitClick ? "not-allowed" : "pointer",
							pointerEvents: "unset"
						}}
					>
						<span className="button-label-with-icon">Submit</span>
						<span>
							<SendIcon className="button-icon" />
						</span>
					</Button>
				</div>
			</>
			<div className="pf-title">VEB Termination Bulk Upload</div>
			<Suspense fallback={<div />}>
				<Paper className="pf-paper-container" elevation={3} variant="outlined">
					<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
						<Grid container spacing={1} className="pf-label">
							<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
								<div className={"pf-label-text required"} id="pf-label-text">
									Upload File
								</div>
							</Grid>
							<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
								{excelFile === null ? (
									<>
										<input
											className={"document-upload"}
											id={"document-upload"}
											name={"file"}
											type="file"
											onChange={handleDocumentChange}
											style={{ display: "none" }}
											accept=".xlsx, .csv"
										/>
										<label htmlFor={"document-upload"}>
											<Button
												className="button"
												component="span"
												variant="contained"
												style={{
													backgroundColor: "#3A4652",
													color: "#ffff",
													width: 210
												}}
											>
												<span className="button-label-with-icon" style={{ color: "#ffff" }}>
													Upload File
												</span>
												<span>
													<PublishIcon className="button-icon" style={{ color: "#ffff" }} />
												</span>
											</Button>
											<span
												className="demo-file-label"
												style={{
													cursor: "pointer",
													fontSize: "0.9rem",
													marginLeft: "0.8rem"
												}}
											>
												<a
													href={memberBulkUploadTemplateFile}
													download="VEB Termination Bulk Upload Template File"
													style={{
														textDecoration: "none"
													}}
												>
													(Download Template File)
												</a>
											</span>
										</label>
									</>
								) : (
									<Grid item className="chipset" xl={4} lg={4} md={12} sm={12} xs={12}>
										<Tooltip title={"Click to download"} arrow>
											<Chip
												label={excelFile ? shortenFileName(excelFile.name) : "No files added"}
												onDelete={() => handleDeleteDocument()}
												onClick={() => downloadFile(excelFile)}
											/>
										</Tooltip>
									</Grid>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Paper>
			</Suspense>
		</div>
	);
};

export default VEBTermination;
